import * as Sentry from '@sentry/react'
import type { createBrowserHistory } from 'history'
import { Config } from '../config'
import type { Account } from '../context/AccountContext'
import type { Scope } from '../context/AuthenticationContext'
import { toError } from '@app/src/errors/toError'
import { markAsReported, shouldReportApiFailure } from './sentry/eventDecision'
import { normalizeEndpoint } from './sentry/fingerprint'
import { buildInitOptions, isReportingEnvironment } from './sentry/initOptions'

export interface ErrorContext {
  tags?: Record<string, string>
  extra?: Record<string, unknown>
  level?: 'error' | 'warning' | 'info' | 'debug' | 'fatal'
  user?: { id?: string; email?: string }
  fingerprint?: string[]
}

let isInitialized = false

const init = (history: ReturnType<typeof createBrowserHistory>, routes: unknown): void => {
  if (isInitialized || !isReportingEnvironment(Config.FRONT_ENV)) {
    return
  }

  Sentry.init({
    ...buildInitOptions({
      dsn: Config.SENTRY_DSN ?? '',
      frontEnv: Config.FRONT_ENV,
      version: import.meta.env.VITE_APP_VERSION,
      apiUrl: Config.API_URL,
    }),

    integrations: [
      Sentry.reactRouterV5BrowserTracingIntegration({
        history,
        routes: routes as Parameters<typeof Sentry.reactRouterV5BrowserTracingIntegration>[0]['routes'],
      }),

      Sentry.replayIntegration({
        maskAllInputs: true,
        blockAllMedia: false,
      }),
    ],
  })

  isInitialized = true
}

/**
 * A string argument used to go out as captureMessage, which produces an event with no
 * stack trace at all - the reason so many issues could not be traced back to a callsite.
 * Wrapping it in an Error costs nothing and keeps the calling frame.
 */
const captureError = (error: Error | string, context: ErrorContext = {}): string => {
  const exception = toError(error, 'Unknown error')

  const eventId = Sentry.captureException(exception, {
    tags: context.tags,
    extra: context.extra,
    level: context.level,
    user: context.user,
    fingerprint: context.fingerprint,
  })

  markAsReported(exception)

  return eventId
}

/**
 * Records an API failure as breadcrumb context on whatever error is reported next,
 * and reports it as its own event only when the backend did not already report it.
 */
const captureApiFailure = (input: {
  error: Error
  method: string
  endpoint: string
  statusCode: number
  backendSentryId?: string
  errorCode?: string
}): void => {
  const endpoint = normalizeEndpoint(input.endpoint)

  // The SDK's own fetch breadcrumb already records method, url and status for every
  // failed request, so only add one when we have something it cannot see.
  if (input.errorCode || input.backendSentryId) {
    Sentry.addBreadcrumb({
      category: 'api',
      type: 'http',
      level: input.statusCode >= 500 ? 'error' : 'warning',
      message: `${input.method} ${endpoint} → ${input.statusCode}`,
      data: {
        ...(input.errorCode ? { errorCode: input.errorCode } : {}),
        ...(input.backendSentryId ? { backendSentryId: input.backendSentryId } : {}),
      },
    })
  }

  if (!shouldReportApiFailure({ statusCode: input.statusCode, hasBackendSentryId: !!input.backendSentryId })) {
    markAsReported(input.error)
    return
  }

  captureError(input.error, {
    tags: {
      errorType: 'api',
      method: input.method,
      endpoint,
      statusCode: input.statusCode.toString(),
      ...(input.errorCode ? { errorCode: input.errorCode } : {}),
    },
    level: 'error',
  })
}

const setUserContext = (account?: Account, scope?: Scope): void => {
  if (!account) {
    Sentry.setUser(null)
    return
  }

  Sentry.setUser({
    id: account.user.id.toString(),
    auth0UserId: account.user.auth0UserId,
  })

  if (account.organization?.id) {
    Sentry.setTag('organizationId', account.organization.id.toString())
    Sentry.setTag('organizationName', account.organization.name)
  }

  if (scope?.solution) {
    Sentry.setTag('solution', scope.solution)
  }

  if (scope?.role) {
    Sentry.setTag('userRole', scope.role)
  }

  Sentry.setContext('account', {
    userId: account.user.id,
    permissions: account.permissions,
  })

  Sentry.setContext('session', {
    currentRole: scope?.role,
    currentSolution: scope?.solution,
    currentOrganizationScope: scope?.organization,
  })
}

const sentryService = {
  init,
  captureError,
  captureApiFailure,
  setUserContext,
}

export default sentryService
